<template>
  <v-row class="facility-details">
    <v-col cols="12" lg="7" class="facility-details__main-data">
      <v-row no-gutters>
        <v-col v-if="isReadonly" cols="12" class="mb-4">
          <InfoBox
            class="mr-2"
            :text="
              $t(
                'message.facilityDetails.addingFacilityToGoogleMapsInProgressInfoText'
              )
            "
          />
        </v-col>
        <v-col cols="12" class="d-flex justify-space-between align-center mb-4">
          <h3 class="facility-details__heading">
            {{ $t("message.common.facilityData") }}
          </h3>
          <div>
            <ui-button
              v-if="formsEdit"
              @click="onFormCancelClick"
              color="primary"
              outlined
            >
              {{ $t("message.common.cancel") }}
            </ui-button>
            <ui-button
              color="primary"
              class="ml-2"
              v-if="isOwner"
              :icon="!formsEdit ? 'icon-edit' : 'icon-check'"
              @click="handleFormSubmit"
              :disabled="isReadonly"
            >
              {{
                !formsEdit
                  ? $t("message.common.edit")
                  : $t("message.common.save")
              }}
            </ui-button>
          </div>
        </v-col>
      </v-row>
      <div class="position-relative">
        <div class="section mt-2">
          <div class="facility-details__address-wrapper">
            <div class="facility-details__map-details">
              <component
                v-for="field in visibleMapFields"
                :is="field.multiline ? 'v-textarea' : 'v-text-field'"
                :key="field.id"
                v-model="field.value"
                :label="field.label"
                :disabled="isFormFieldDisabled(field)"
                v-bind="
                  field.multiline
                    ? { rows: 1, autoGrow: true }
                    : { autoGrow: true }
                "
                :class="
                  field.multiline
                    ? 'facility-details__map-field--multiline'
                    : ''
                "
              ></component>
            </div>
            <div
              v-if="(areCoordsValid || googleMapsUrl) && !isAreaFacility"
              class="facility-details__map-wrapper"
            >
              <div class="facility-details__map" v-if="areCoordsValid">
                <Map
                  :coords="[coords]"
                  :center="{ icon: geoCoords }"
                  defaultStyles="height: 402px;"
                  :options="mapOptions"
                >
                  <template #marker>
                    <MapMarker
                      :icon="coords.icon"
                      :markerType="coords.marker"
                    />
                  </template>
                </Map>
              </div>
              <div v-if="areCoordsValid" class="map-btns-wrapper">
                <MapButton
                  v-if="googleMapsUrl"
                  :url="googleMapsUrl"
                  :label="$t('message.facilityDetails.mapButton')"
                  icon="google"
                />
                <MapButton
                  v-if="bingMapsUrl && showBingMapsButton"
                  :url="bingMapsUrl"
                  :label="$t('message.facilityDetails.bingMapButton')"
                  icon="bing"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="section facility-details__contact">
          <div class="section-title">
            {{ $t("message.common.contact") }}
          </div>

          <v-text-field
            v-for="field in additionalFields"
            :key="field.id"
            :label="field.label"
            v-model="field.value"
            :disabled="!formsEdit || !getPermission(field.permission)"
          >
          </v-text-field>
        </div>

        <div class="section facility-details__other">
          <div class="section-title">
            {{ $t("message.common.other") }}
          </div>
          <div class="section facility-details__opening-date">
            <div class="mb-0">
              {{ $t("message.facilityDetails.startDate") }}
            </div>
            <v-row no-gutters>
              <v-col cols="3" class="mr-4">
                <v-text-field
                  :label="openingDate.year.label"
                  v-model="openingDate.year.value"
                  :disabled="
                    !formsEdit || !getPermission(openingDate.year.permission)
                  "
                  v-validate="'required|digits:4'"
                  :error-messages="errors.collect(openingDate.year.label)"
                  :data-vv-name="openingDate.year.label"
                  type="text"
                >
                </v-text-field>
              </v-col>
              <v-col cols="3" class="mr-4">
                <v-select
                  :label="openingDate.month.label"
                  v-model="openingDate.month.value"
                  :disabled="
                    !formsEdit || !getPermission(openingDate.month.permission)
                  "
                  v-validate="'required'"
                  :error-messages="errors.collect(openingDate.month.label)"
                  :data-vv-name="openingDate.month.label"
                  item-text="name"
                  item-value="id"
                  :items="openingDate.month.list"
                >
                </v-select>
              </v-col>
              <v-col cols="3" class="mr-4">
                <v-select
                  :label="openingDate.day.label"
                  v-model="openingDate.day.value"
                  :disabled="
                    !formsEdit || !getPermission(openingDate.day.permission)
                  "
                  :items="daysOfMonth"
                >
                </v-select>
              </v-col>
            </v-row>

            <InfoBox
              class="mr-2"
              :text="$t('message.facilityDetails.validation.date')"
            />
          </div>

          <div class="section facility-details__description">
            <div>
              {{ $t("message.common.description") }}
            </div>

            <v-textarea
              :label="description.label"
              v-model="description.value"
              :disabled="!formsEdit || !getPermission(description.permission)"
              :counter="description.charLimit"
              :rows="1"
              auto-grow
            ></v-textarea>
          </div>
        </div>
        <SectionLoader :status="loading.mainData" :opacity="0.5" />
      </div>

      <div class="section facility-details__url-attributes">
        <!-- URL ATTRIBUTES -->
        <div class="section-title d-flex justify-space-between align-center">
          <div>
            {{ $t("message.facilityDetails.urlAttributes.title") }}
          </div>
          <v-btn
            icon
            color="primary"
            @click="urlAttributesDialog = true"
            v-if="
              urlAttributes && urlAttributes.length && canEditFacilityAttributes
            "
            :disabled="isReadonly"
          >
            <v-icon>
              icon-edit
            </v-icon>
          </v-btn>
        </div>

        <div></div>

        <UrlAttributesPreview
          v-if="urlAttributes && urlAttributes.length"
          :url-attributes="urlAttributes"
        />
        <div class="add-attributes d-flex justify-space-between" v-else>
          <div class="add-url-attributes-message">
            <div class="description__title">
              {{
                $t("message.facilityDetails.urlAttributes.noAttributesSelected")
              }}
            </div>
            <div class="description">
              {{
                $t("message.facilityDetails.urlAttributes.addAttributesInfo")
              }}
            </div>
          </div>
          <ui-button
            color="primary"
            outlined
            icon="icon-plus"
            @click="urlAttributesDialog = true"
            :disabled="!canEditFacilityAttributes || isReadonly"
          >
            {{ $t("message.facilityDetails.urlAttributes.addAttributes") }}
          </ui-button>
        </div>
        <SectionLoader :status="loading.attributes" :opacity="0.5" />
      </div>

      <div class="section facility-details__attributes">
        <!-- ATTRIBUTES -->
        <div class="section-title d-flex justify-space-between align-center">
          <div>
            {{ $t("message.facilityDetails.attributes.facilityAttributes") }}
          </div>
          <v-btn
            icon
            color="primary"
            @click="attributesDialog = true"
            v-if="attributes && attributes.length && canEditFacilityAttributes"
            :disabled="isReadonly"
          >
            <v-icon>
              icon-edit
            </v-icon>
          </v-btn>
        </div>
        <GmbAttributesPreview
          v-if="attributes && attributes.length"
          :facility-attributes="attributes"
        />

        <div class="add-attributes d-flex justify-space-between" v-else>
          <div class="add-attributes-message">
            <div class="description__title">
              {{
                $t("message.facilityDetails.attributes.noAttributesSelected")
              }}
            </div>
            <div class="description">
              {{ $t("message.facilityDetails.attributes.addAttributesInfo") }}
            </div>
          </div>
          <ui-button
            color="primary"
            outlined
            icon="icon-plus"
            @click="attributesDialog = true"
            :disabled="!canEditFacilityAttributes || isReadonly"
          >
            {{ $t("message.facilityDetails.attributes.addAttributes") }}
          </ui-button>
        </div>
        <SectionLoader :status="loading.attributes" :opacity="0.5" />
      </div>

      <div v-if="showServicesSection" class="section">
        <!-- SERVICES -->
        <div class="section-title d-flex justify-space-between align-center">
          <div>
            {{ $t("message.facilityDetails.services.title") }}
          </div>
          <v-btn
            v-if="services && services.length && canEditFacilityServices"
            icon
            color="primary"
            @click="servicesDialog = true"
            :disabled="isReadonly"
          >
            <v-icon>
              icon-edit
            </v-icon>
          </v-btn>
        </div>
        <div
          v-if="!services.length"
          class="add-attributes d-flex justify-space-between"
        >
          <div class="add-attributes-message">
            <div class="description__title">
              {{ $t("message.facilityDetails.services.noServicesSelected") }}
            </div>
            <div class="description">
              {{ $t("message.facilityDetails.services.addServicesInfo") }}
            </div>
          </div>
          <ui-button
            color="primary"
            outlined
            icon="icon-plus"
            @click="servicesDialog = true"
            :disabled="!canEditFacilityServices || isReadonly"
          >
            {{ $t("message.facilityDetails.services.addServices") }}
          </ui-button>
        </div>
        <GmbServicePreview
          v-else
          :service-items="resData.service_items"
          :currency="resData.default_currency"
          @update="handleGmbServicesUpdate"
          @add-to-category="handleAddServiceToCategory"
        />
        <SectionLoader :status="loading.services" :opacity="0.5" />
      </div>

      <SectionLoader :status="loading.view" :opacity="1" />
      <GmbServicesDialog
        v-if="servicesDialog"
        is-open
        :selected-services="services"
        :categories="facilityCategories"
        :currency="resData.default_currency"
        :preselected-services-category="preselectedServicesCategory"
        @update="handleGmbServicesUpdate"
        @close="closeServicesDialog"
      />
    </v-col>

    <v-col cols="12" lg="5" class="facility-details__additional-data">
      <!-- CATEGORIES -->
      <div class="position-relative">
        <div class="section facility-details__primary-category">
          <PrimaryCategoryEdit
            :loading="loading.categories"
            :is-readonly="isReadonly"
            :primary-category="primaryCategory"
            @save="saveCategory"
          />
        </div>

        <div class="section facility-details__additional-categories">
          <AdditionalCategoriesEdit
            :loading="loading.categories"
            :is-readonly="isReadonly"
            :additional-categories="additionalCategories"
            @save="saveCategory"
            @delete="deleteCategory"
          />
        </div>

        <SectionLoader :status="loading.categories" :opacity="0.5" />
      </div>

      <!-- LABELS -->
      <div class="section facility-details__labels">
        <div class="section-title d-flex justify-space-between align-center">
          <div>
            {{ $t("message.facilityDetails.labels.title") }}
          </div>
          <v-btn
            v-if="isOwner && canEditFacilityLabels"
            icon
            color="primary"
            @click="openDialog('labels')"
            :disabled="isReadonly"
          >
            <v-icon>
              icon-edit
            </v-icon>
          </v-btn>
        </div>

        <div v-if="labels.length">
          <v-chip
            v-for="(item, index) in labels"
            :key="`${item}-${index}`"
            outlined
            class="mr-2 my-1"
          >
            {{ item }}
            <v-icon
              v-if="canEditFacilityLabels"
              class="ml-2 mr--1"
              color="grey"
              @click="showLabelDeleteConfirmationDialog(index)"
              :disabled="isReadonly"
            >
              icon-close
            </v-icon>
          </v-chip>
        </div>
        <ui-button
          v-else-if="canEditFacilityLabels"
          color="primary"
          icon="mdi-plus"
          class="my-1"
          outlined
          block
          @click="activeDialog = 'labels'"
          :disabled="isReadonly"
        >
          {{ $t("message.common.add") }}
        </ui-button>

        <v-chip v-else color="grey-darker" class="my-1">
          {{ $t("message.facilityDetails.labels.noLabels") }}
        </v-chip>

        <SectionLoader :status="loading.labels" :opacity="0.5" />
      </div>

      <!-- OPENING HOURS -->
      <div class="section facility-details__opening-hours">
        <div class="section-title d-flex justify-space-between align-center">
          <div>
            {{ $t("message.facilityDetails.openingHours") }}
          </div>
          <v-btn
            v-if="isOwner && canEditFacilityHours"
            icon
            color="primary"
            @click="openDialog('openingHours')"
            :disabled="isReadonly"
          >
            <v-icon>
              icon-edit
            </v-icon>
          </v-btn>
        </div>
        <ul class="pl-0">
          <li
            v-for="item in openingHours.days"
            :key="item.id"
            class="d-flex justify-space-between mb-2"
          >
            <p class="mb-2">
              {{ item.name }}
            </p>
            <template v-if="!item.value">
              <span class="grey--text">
                {{ $t("message.common.noData") }}
              </span>
            </template>
            <template v-else>
              <ul v-if="item.value.length > 0" class="pl-0">
                <li
                  class="text-right"
                  v-for="(hours, index) in item.value"
                  :key="`hours-${index}`"
                >
                  {{ formatOpeningHours(hours) }}
                </li>
              </ul>
              <span class="grey--text" v-else>{{
                $t("message.common.closed")
              }}</span>
            </template>
          </li>
        </ul>

        <SectionLoader :status="loading.openingHours" :opacity="0.5" />
      </div>

      <div class="section facility-details__custom-opening-hours">
        <div class="section-title d-flex justify-space-between align-center">
          <div>
            {{ $t("message.facilityDetails.customOpeningHours") }}
          </div>
          <v-btn
            v-if="isOwner && canEditFacilityHours"
            icon
            color="primary"
            @click="openDialog('specialHours')"
            :disabled="isReadonly"
          >
            <v-icon>
              icon-edit
            </v-icon>
          </v-btn>
        </div>
        <template v-if="specialHours.days.length > 0">
          <ul class="pl-0">
            <li
              v-for="item in specialHours.days"
              :key="item.formatted_date"
              class="d-flex justify-space-between"
            >
              <p class="mb-2">
                {{ item.formatted_date }}
              </p>
              <ul class="pl-0">
                <li
                  class="text-right mb-2"
                  v-for="(hours, index) in item.value"
                  :key="`special_hours-${index}`"
                >
                  <span v-if="item.is_closed" class="grey--text">
                    {{ $t("message.common.closed") }}
                  </span>
                  <span v-else>
                    {{ formatOpeningHours(hours) }}
                  </span>
                </li>
              </ul>
            </li>
          </ul>
        </template>
        <template v-else>
          <p class="grey--text">{{ $t("message.common.noData") }}</p>
        </template>

        <SectionLoader :status="loading.specialOpeningHours" :opacity="0.5" />
      </div>

      <!-- MORE HOURS -->
      <div class="section facility-details__more-hours">
        <MoreHoursEdit
          :loading="loading.moreHours"
          :is-owner="isOwner"
          :is-readonly="isReadonly"
          :facilities-ids="[locationId]"
          :items="moreHours"
          @save="saveMoreHours"
        />
      </div>

      <!-- OPEN STATUS -->
      <div class="section facility-details__open-status">
        <div class="section-title d-flex justify-space-between align-center">
          <div>
            {{ $t("message.common.openCloseFacility") }}
          </div>
          <v-btn
            v-if="currentOpenStatus && canEditFacilityOpenStatus"
            icon
            color="primary"
            @click="openDialog('openCloseFacility')"
            :disabled="isReadonly"
          >
            <v-icon>
              icon-edit
            </v-icon>
          </v-btn>
        </div>

        <v-chip class="status--general" v-if="currentOpenStatus">
          <FacilityStatusItem
            :type="currentOpenStatus"
            :label="$t(`message.openStatusTypes.${currentOpenStatus}`)"
          />
        </v-chip>

        <SectionLoader :status="loading.openStatus" :opacity="0.5" />
      </div>

      <!-- LOCATION STATUS -->
      <div class="section facility-details__location-status">
        <div class="section-title d-flex justify-space-between align-center">
          <div>
            {{ $t("message.common.statusOnGMB") }}
          </div>
        </div>

        <v-chip class="status--general" v-if="locationStatus">
          <FacilityStatusItem
            :code="locationStatus.code"
            :label="locationStatus.state"
          />
        </v-chip>
      </div>
    </v-col>

    <!-- DIALOGS PART BELOW -->

    <UrlAttributesDialog
      :is-open="urlAttributesDialog"
      :facilities-ids="[$route.params.id]"
      :initial-url-attributes="urlAttributes"
      @close="urlAttributesDialog = false"
      @save="onUrlAttributesSave"
    />

    <DialogGmbAttributesTree
      v-if="attributesDialog"
      :facilities-ids="[$route.params.id]"
      :facility-attributes="attributes"
      :is-open="attributesDialog"
      @close="attributesDialog = false"
      @save="onAttributesSave"
    />

    <EditLabelsDialog
      :is-open="activeDialog === 'labels'"
      :labels="labels"
      @close="activeDialog = null"
      @save="saveLabels"
    />

    <v-dialog v-model="dialogDeleteLabel" max-width="450">
      <v-card class="pa-6">
        <p class="mb-8 text-center">
          {{ $t("message.facilityDetails.labels.deleteLabelDialogTitle") }}
        </p>

        <div class="d-flex justify-center">
          <ui-button
            outlined
            class="mr-3"
            color="primary"
            @click="hideLabelDeleteConfirmationDialog"
          >
            {{ $t("message.common.cancel") }}
          </ui-button>
          <ui-button color="primary" @click="deleteLabel(deleteLabelIndex)">
            {{ $t("message.common.confirmDelete") }}
          </ui-button>
        </div>
      </v-card>
    </v-dialog>

    <OpeningHoursDialog
      v-if="activeDialog === 'openingHours'"
      :days="openingHours.days"
      :item="activeDialogData"
      :maxWidth="1154"
      @close="activeDialog = null"
      @save="saveOpeningHours"
    />

    <SpecialHoursDialog
      v-if="activeDialog === 'specialHours'"
      :days="specialHours.days"
      :maxWidth="1154"
      :item="activeDialogData"
      @close="activeDialog = null"
      @save="saveSpecialHours"
    />

    <OpenCloseFacilityDialog
      :isOpen="activeDialog === 'openCloseFacility'"
      :maxWidth="1154"
      :current-open-status="currentOpenStatus"
      @save="saveOpenStatus"
      @close="activeDialog = null"
    />

    <SectionLoader :status="loading.view" :opacity="1" />
  </v-row>
</template>

<script>
import facilitiesService from "@/services/facilities";
import serviceHandler from "@/helpers/handlers/service";
import { primary } from "@/config/marker.js";
import { capitalize } from "@/helpers/helpers.js";
import ErrorHandler from "@/helpers/handlers/error";
import { upperFirst, groupBy } from "lodash";

import cuid from "cuid";
import moment from "moment";
import { range } from "lodash";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { gtmEvent__singleLocationEditSummary } from "@/helpers/gtm-events";
import { dev__warnNotify } from "@/helpers/helpers";
import isEqual from "lodash/isEqual";
import { CategoryType } from "@/enums/category-type";
import { GmbAttributeValueType } from "@/enums/gmb-attribute-value-type";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { FORMAT } from "@/config/date-format";

import Map from "@/components/ui/Map/Map.vue";
import MapMarker from "@/components/ui/Map/Marker.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import FacilityStatusItem from "@/components/BusinessAddresses/FacilityStatusItem.vue";
import UrlAttributesPreview from "@/components/FacilityDetails/GmbUrlAttributes/UrlAttributesPreview";
import UrlAttributesDialog from "@/components/FacilityDetails/GmbUrlAttributes/UrlAttributesDialog";
import GmbAttributesPreview from "@/components/FacilityDetails/GmbAttributes/GmbAttributesPreview";
import PrimaryCategoryEdit from "@/components/FacilityDetails/GmbCategories/PrimaryCategory";
import AdditionalCategoriesEdit from "@/components/FacilityDetails/GmbCategories/AdditionalCategories";
import MoreHoursEdit from "@/components/FacilityDetails/GmbMoreHours/MoreHours";
import DialogGmbAttributesTree from "@/components/FacilityDetails/GmbAttributes/GmbAttributesTree";
import GmbServicesDialog from "@/components/FacilityDetails/GmbServices/GmbServicesDialog.vue";
import EditLabelsDialog from "@/components/FacilityDetails/Dialog/EditLabelsDialog.vue";
import OpeningHoursDialog from "@/components/FacilityDetails/Dialog/OpeningHoursDialog.vue";
import SpecialHoursDialog from "@/components/FacilityDetails/Dialog/SpecialHoursDialog.vue";
import OpenCloseFacilityDialog from "@/components/FacilityDetails/Dialog/OpenCloseFacilityDialog.vue";
import MapButton from "@/components/FacilityDetails/MapButton.vue";
import InfoBox from "@/components/ui/InfoBox.vue";
import gmbUpdateVerification from "@/helpers/mixins/gmb-update-verification";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import GmbServicePreview from "@/components/FacilityDetails/GmbServices/GmbServicePreview.vue";
import { BUSINESS_TYPE } from "@/enums/business-type.enum";
export default {
  name: "FacilityDetailsTab",
  components: {
    Map,
    MapMarker,
    SectionLoader,
    FacilityStatusItem,
    UrlAttributesPreview,
    UrlAttributesDialog,
    GmbAttributesPreview,
    PrimaryCategoryEdit,
    AdditionalCategoriesEdit,
    MoreHoursEdit,
    DialogGmbAttributesTree,
    EditLabelsDialog,
    OpeningHoursDialog,
    SpecialHoursDialog,
    OpenCloseFacilityDialog,
    MapButton,
    InfoBox,
    GmbServicesDialog,
    GmbServicePreview
  },
  mixins: [permissionsInfo, gmbUpdateVerification],
  data() {
    return {
      moment,

      // GENERAL
      resData: null,
      formsEdit: false,
      isOwner: true,
      isReadonly: false,
      loading: {
        view: false,
        mainData: false,
        attributes: false,
        categories: false,
        labels: false,
        openingHours: false,
        specialOpeningHours: false,
        moreHours: false,
        openStatus: false,
        services: false
      },

      // DIALOGS
      activeDialog: "",
      dialogDeleteLabel: false,

      // MAP
      geoCoords: [],
      googleMapsUrl: "",
      bingMapsUrl: "",
      mapOptions: {
        maxZoom: 18,
        minZoom: 18,
        url:
          "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        attribution:
          '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        options: {
          dragging: false,
          zoomControl: false,
          doubleClickZoom: false
        }
      },

      // MAIN FIELDS
      description: {
        label: this.$t("message.facilityDetails.facilityDescription"),
        value: "",
        charLimit: 750,
        permission: "canEditFacilityDescription"
      },
      openingDate: {
        year: {
          label: this.$t("message.common.year"),
          value: "",
          permission: "canEditFacilityOpeningDate"
        },
        month: {
          label: this.$t("message.common.month"),
          value: "",
          list: moment.months(true).map((item, index) => {
            return { id: index + 1, name: capitalize(item) };
          }),
          permission: "canEditFacilityOpeningDate"
        },
        day: {
          label: this.$t("message.common.day"),
          value: "",
          permission: "canEditFacilityOpeningDate"
        },
        res: null
      },
      mapFields: {
        facilityName: {
          label: this.$t("message.common.facilityName"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityName"
        },
        street: {
          label: this.$t("message.common.street"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityAddress",
          disabledForAreas: true
        },
        localNumber: {
          label: this.$t("message.facilityDetails.forms.streetAndHomeNumber"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityAddress",
          disabledForAreas: true
        },
        postalCode: {
          label: this.$t("message.common.postalCode"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityAddress",
          disabledForAreas: true
        },
        city: {
          label: this.$t("message.common.city"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityAddress",
          disabledForAreas: true
        },
        country: {
          label: this.$t("message.common.country"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityAddress",
          disabledForAreas: true
        },
        storeCode: {
          label: this.$t("message.common.storeCode"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityStoreCode",
          disabledForAreas: true
        },
        serviceAreas: {
          label: this.$t("message.common.areas"),
          id: cuid(),
          value: "",
          multiline: true,
          permission: "canEditFacilityAddress",
          disabled: true
        }
      },
      additionalFields: {
        website: {
          label: this.$t("message.facilityDetails.forms.websiteAddress"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityWebsite"
        },
        phoneOne: {
          label: this.$t("message.facilityDetails.forms.phoneOne"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityPhoneNumber"
        },
        phoneTwo: {
          label: this.$t("message.facilityDetails.forms.phoneTwo"),
          id: cuid(),
          value: "",
          permission: "canEditFacilityPhoneNumber"
        }
      },

      // URL ATTRIBUTES
      urlAttributes: [],
      urlAttributesDialog: false,

      // ATTRIBUTES
      attributes: [],
      attributesDialog: false,

      //SERVICES
      services: [],
      servicesDialog: false,
      preselectedServicesCategory: null,
      // CATEGORIES
      primaryCategory: null,
      additionalCategories: [],
      chosenCategoryType: null,

      // LABELS
      labels: [],
      deleteLabelIndex: null,

      // OPENING HOURS
      openingHours: {
        name: "openingHours",
        title: this.$t("message.facilityDetails.openingHours"),
        days: {
          monday: {
            name: this.$t("message.common.monday"),
            value: null,
            id: cuid()
          },
          tuesday: {
            name: this.$t("message.common.tuesday"),
            value: null,
            id: cuid()
          },
          wednesday: {
            name: this.$t("message.common.wednesday"),
            value: null,
            id: cuid()
          },
          thursday: {
            name: this.$t("message.common.thursday"),
            value: null,
            id: cuid()
          },
          friday: {
            name: this.$t("message.common.friday"),
            value: null,
            id: cuid()
          },
          saturday: {
            name: this.$t("message.common.saturday"),
            value: null,
            id: cuid()
          },
          sunday: {
            name: this.$t("message.common.sunday"),
            value: null,
            id: cuid()
          }
        }
      },
      specialHours: {
        name: "specialHours",
        title: this.$t("message.facilityDetails.customOpeningHours"),
        days: []
      },
      moreHours: [],

      // OPEN STATUS
      currentOpenStatus: null,

      // LOCATION STATUS
      locationStatus: null
    };
  },

  async created() {
    this.loading.view = true;
    await this.fetchFacilityData();
    if (!this.resData) {
      await this.$router.push({ name: "business-addresses" });
      return;
    }

    this.$gtm.trackCustomEvent({
      event: GTM_EVENT_NAME.LOCATION_DETAILS_ACTION,
      location_id: this.locationId,
      action: "tab_loc_details"
    });

    this.loading.view = false;
  },

  computed: {
    isAreaFacility() {
      return this.resData.business_type === BUSINESS_TYPE.AREA;
    },
    facilityCategories() {
      return this.resData
        ? [this.resData.primary_category, ...this.resData.additional_categories]
        : [];
    },
    showServicesSection() {
      return isFeatureFlagEnabled(FEATURE_FLAG.CN_5721_SERVICES);
    },
    showBingMapsButton() {
      return isFeatureFlagEnabled(FEATURE_FLAG.CN_5335_BING_MAPS_LINK);
    },
    coords() {
      return {
        icon: this.geoCoords,
        marker: primary
      };
    },
    visibleMapFields() {
      if (!this.resData) return {};
      const fields = Object.entries(this.mapFields)
        .filter(([key, value]) => {
          if (key === "serviceAreas") {
            return this.resData.business_type !== 1;
          }
          return true;
        })
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});

      return fields;
    },
    areCoordsValid() {
      return (
        this.geoCoords &&
        this.geoCoords.length > 0 &&
        !this.geoCoords.every(coord => coord === 0)
      );
    },
    activeDialogData() {
      return this.$data[this.activeDialog];
    },
    daysOfMonth() {
      if (this.openingDate.month.value && this.openingDate.year.value) {
        const days = moment(this.openingDate.year.value)
          .month(this.openingDate.month.value - 1)
          .daysInMonth();
        return [this.$t("message.common.lack"), ...range(1, days + 1)];
      }
      return [this.$t("message.common.lack"), ...range(1, 32)];
    },
    locationId() {
      return this.$route.params && this.$route.params.id
        ? +this.$route.params.id
        : null;
    }
  },

  methods: {
    isFormFieldDisabled(field) {
      return (
        !this.formsEdit ||
        !this.getPermission(field.permission) ||
        (this.isAreaFacility && field.disabledForAreas) ||
        field.disabled
      );
    },
    handleAddServiceToCategory(category) {
      this.preselectedServicesCategory = category;
      this.servicesDialog = true;
    },
    closeServicesDialog() {
      this.setServices(this.resData.service_items);
      this.preselectedServicesCategory = null;
      this.servicesDialog = false;
    },
    handleGmbServicesUpdate(data) {
      this.setInitialData(data);
    },
    openDialog(dialogNumber) {
      this.activeDialog = dialogNumber;
    },
    setInitialData(data) {
      this.resData = data;
      this.fillFormsWithData(data);
      this.geoCoords = data.geo_coords;
      this.isReadonly = data.facility_listings.address_group === null;
      this.setAttributes(data.attributes);
      this.setServices(data.service_items);
      this.labels = data.labels;
      this.currentOpenStatus = data.open_status;
      this.locationStatus = data.location_state;
      this.setMoreHours(data.more_hours);
    },
    async fetchFacilityData() {
      const data = await serviceHandler(
        facilitiesService.getFacilityData(this.$route.params.id)
      );

      if (data) {
        this.setInitialData(data);
      }
    },

    fillGeneralForms(data) {
      const {
        street,
        localNumber,
        postalCode,
        city,
        country,
        storeCode,
        serviceAreas
      } = this.mapFields;
      const { website, phoneOne, phoneTwo } = this.additionalFields;

      this.mapFields.facilityName.value = data.name;

      this.openingDate.year.value =
        data.open_info && data.open_info.openingDate
          ? data.open_info.openingDate.year
          : "";
      this.openingDate.month.value =
        data.open_info && data.open_info.openingDate
          ? data.open_info.openingDate.month
          : "";
      this.openingDate.day.value =
        data.open_info &&
        data.open_info.openingDate &&
        data.open_info.openingDate.day
          ? data.open_info.openingDate.day
          : this.daysOfMonth[0];
      if (data.open_info) {
        this.openingDate.res = {
          canReopen: data.open_info.canReopen,
          status: data.open_info.status
        };
      }

      this.description.value = data.description;

      this.primaryCategory = data.primary_category;
      this.additionalCategories = data.additional_categories;

      this.googleMapsUrl = data.google_maps_url;
      this.bingMapsUrl = data.bing_maps_url;
      street.value = data.street;
      localNumber.value = data.street_number;
      postalCode.value = data.postal_code;
      city.value = data.city;
      country.value = data.country;
      serviceAreas.value =
        data.service_areas && data.service_areas.length > 0
          ? data.service_areas.map(area => area.name).join(", ")
          : null;
      storeCode.value = data.store_code;
      website.value = data.website;
      phoneOne.value = data.phone_number;
      phoneTwo.value = data.phone_number_2;
    },

    fillFormsWithData(data) {
      this.fillGeneralForms(data);

      const {
        monday,
        tuesday,
        wednesday,
        thursday,
        friday,
        saturday,
        sunday
      } = this.openingHours.days;

      if (data.opening_hours) {
        const days = this.openingHours.days;

        Object.entries(days).forEach(([k, v]) => {
          const upperKey = upperFirst(k);

          days[k].value = data.opening_hours[upperKey] || [];
        });
      }
      this.specialHours.days = data.special_hours
        ? this.formatSpecialHours(data.special_hours)
        : [];
    },

    async handleFormSubmit() {
      let openingDatePayload = {};

      if (this.openingDate.res) {
        openingDatePayload = {
          ...this.res
        };
      }

      openingDatePayload.openingDate = {
        year: this.openingDate.year.value,
        month: this.openingDate.month.value
      };

      if (this.openingDate.day.value !== this.$t("message.common.lack")) {
        openingDatePayload.openingDate.day = this.openingDate.day.value;
      }

      if (this.formsEdit) {
        this.loading.mainData = true;

        const payload = {};

        if (this.canEditFacilityName) {
          payload.name = this.mapFields.facilityName.value;
        }

        if (this.canEditFacilityAddress) {
          payload.street = this.mapFields.street.value;
          payload.street_number = this.mapFields.localNumber.value;
          payload.postal_code = this.mapFields.postalCode.value;
          payload.city = this.mapFields.city.value;
          payload.country = this.mapFields.country.value;
        }

        if (this.canEditFacilityStoreCode) {
          payload.store_code = this.mapFields.storeCode.value;
        }

        if (this.canEditFacilityWebsite) {
          payload.website = this.additionalFields.website.value;
        }

        if (this.canEditFacilityPhoneNumber) {
          payload.phone_number = this.additionalFields.phoneOne.value;
          payload.phone_number_2 = this.additionalFields.phoneTwo.value;
        }

        if (this.canEditFacilityDescription) {
          payload.description = this.description.value;
        }

        if (this.canEditFacilityOpeningDate) {
          payload.open_info = openingDatePayload;
        }

        // GTM event data collection below
        let changedFields = [];
        try {
          Object.keys(payload).forEach(key => {
            if (
              key in this.resData &&
              !isEqual(payload[key], this.resData[key])
            ) {
              changedFields.push(key);
            }
          });

          changedFields.forEach(field => {
            this.$gtm.trackCustomEvent({
              event: GTM_EVENT_NAME.SINGLE_LOCATION_EDIT_BASIC_DETAILS,
              location_id: this.locationId,
              field,
              old_value:
                field !== "open_info"
                  ? this.resData[field]
                  : moment({
                      ...this.resData[field].openingDate,
                      month: this.resData[field].openingDate.month - 1
                    }).format(FORMAT.DATE),
              new_value:
                field !== "open_info"
                  ? payload[field]
                  : moment({
                      ...payload[field].openingDate,
                      month: payload[field].openingDate.month - 1
                    }).format(FORMAT.DATE)
            });
          });

          gtmEvent__singleLocationEditSummary({
            locationId: this.$route.params.id,
            fields: changedFields
          });
        } catch (error) {
          dev__warnNotify(error);
        }
        // end GTM event stuff

        await this.submitUpdate(payload, changedFields);
        this.loading.mainData = false;
      }

      this.formsEdit = !this.formsEdit;
    },

    submitUpdate(payload, updatedFields = []) {
      this.checkGmbVerificationFields(
        updatedFields,
        () => this.saveFacilityDetails(payload),
        () => (this.formsEdit = true)
      );
    },

    async saveFacilityDetails(payload) {
      await facilitiesService
        .patchFacility(this.$route.params.id, payload)
        .then(response => {
          if (response) {
            this.$notify({
              group: "default",
              type: "success",
              text: this.$t("message.notification.changesSaved")
            });
          }
        })
        .catch(error => {
          if (error && error.response && error.response.status === 400) {
            this.$gtm.trackCustomEvent({
              event: GTM_EVENT_NAME.LOCATION_PROCESSING_IN_PROGRESS,
              location_id: this.locationId,
              num_locations: 1,
              action: "single_loc_edit"
            });
          }
          ErrorHandler.displayError(error);
        })
        .finally(async () => {
          await this.fetchFacilityData();
        });
    },

    onFormCancelClick() {
      this.fillGeneralForms(this.resData);
      this.setAttributes(this.resData.attributes);

      this.formsEdit = false;
    },

    getPermission(permissionName) {
      return this[permissionName];
    },

    goToFacilitiesList() {
      this.$router.push({ name: "business-addresses-list" });
    },

    // ---------------------------------------
    // URL ATTRIBUTES
    async onUrlAttributesSave(attributes) {
      this.setAttributes(attributes, { saveUrl: true });

      if (this.urlAttributes) {
        await this.saveAttributesData();
      }
    },

    // ---------------------------------------
    // ATTRIBUTES
    async saveAttributesData() {
      this.loading.attributes = true;
      const payload = {};

      if (this.canEditFacilityAttributes) {
        payload.attributes = this.getAttributesUpdateData();

        // GTM event data collection below
        try {
          gtmEvent__singleLocationEditSummary({
            locationId: this.$route.params.id,
            fields: "attributes"
          });

          const updatedAttributes = payload.attributes.map(attribute => {
            return {
              id: attribute.gmb_attribute.attribute_id,
              values: attribute.values
            };
          });

          const initialAttributes = this.resData.attributes.map(attribute => {
            return {
              id: attribute.gmb_attribute.attribute_id,
              values: attribute.values
            };
          });

          this.getChangedAttributes(
            updatedAttributes,
            initialAttributes
          ).forEach(attribute => {
            this.$gtm.trackCustomEvent({
              event: GTM_EVENT_NAME.SINGLE_LOCATION_EDIT_ATTRIBUTES_DETAILS,
              location_id: this.locationId,
              attribute_id: attribute
            });
          });
        } catch (error) {
          dev__warnNotify(error);
        }
        // end GTM event stuff

        await this.saveFacilityDetails(payload);
      }

      this.loading.attributes = false;
    },

    setServices(serviceItems) {
      this.services = serviceItems
        .map(service => {
          return service.items.map(item => {
            return {
              ...item,
              selected: true
            };
          });
        })
        .flat();
    },
    setAttributes(attributes, { saveUrl = false, saveOther = false } = {}) {
      const urlAttributes = attributes.filter(
        attribute =>
          attribute.gmb_attribute.value_type === GmbAttributeValueType.URL
      );

      if (saveUrl || (urlAttributes && urlAttributes.length)) {
        this.urlAttributes = urlAttributes;
      }

      const nonUrlAttributes = attributes.filter(
        attribute =>
          attribute.gmb_attribute.value_type !== GmbAttributeValueType.URL
      );

      if (saveOther || (nonUrlAttributes && nonUrlAttributes.length)) {
        this.attributes = nonUrlAttributes;
      }
    },

    getAttributesUpdateData() {
      const updatedAttributes = [...this.attributes, ...this.urlAttributes];

      return updatedAttributes.map(
        ({ gmb_attribute: { attribute_id }, values }) => ({
          gmb_attribute: { attribute_id },
          values
        })
      );
    },

    async onAttributesSave(attributes) {
      this.setAttributes(attributes, { saveOther: true });

      if (this.attributes) {
        await this.saveAttributesData();
      }
    },

    getChangedAttributes(updatedAttributes, initialAttributes) {
      const attributesChanged = [];

      updatedAttributes.forEach(attribute => {
        if (
          !isEqual(
            attribute.values,
            initialAttributes.find(att => att.id === attribute.id)
              ? initialAttributes.find(att => att.id === attribute.id).values
              : null
          )
        ) {
          if (!attributesChanged.includes(attribute.id)) {
            attributesChanged.push(attribute.id);
          }
        }
      });
      initialAttributes.forEach(attribute => {
        if (
          !isEqual(
            attribute.values,
            updatedAttributes.find(att => att.id === attribute.id)
              ? updatedAttributes.find(att => att.id === attribute.id).values
              : null
          )
        ) {
          if (!attributesChanged.includes(attribute.id)) {
            attributesChanged.push(attribute.id);
          }
        }
      });

      return attributesChanged;
    },

    // ---------------------------------------
    // CATEGORIES
    async saveCategory({ category, type }) {
      this.loading.categories = true;
      this.chosenCategoryType = type;

      const params = {};

      if (
        this.chosenCategoryType === CategoryType.PRIMARY &&
        this.canEditFacilityPrimaryCategory
      ) {
        params.primary_category = category.id;

        gtmEvent__singleLocationEditSummary({
          locationId: this.$route.params.id,
          fields: "primary_category"
        });

        await this.submitUpdate(params, ["primary_category"]);
      } else if (
        this.chosenCategoryType === CategoryType.ADDITIONAL &&
        this.canEditFacilityAdditionalCategories
      ) {
        const additionalCategoriesIds = this.additionalCategories.map(
          cat => cat.id
        );
        additionalCategoriesIds.push(category.id);
        params.additional_categories = additionalCategoriesIds;

        gtmEvent__singleLocationEditSummary({
          locationId: this.$route.params.id,
          fields: "additional_categories"
        });

        await this.submitUpdate(params, ["additional_categories"]);
      }

      this.chosenCategoryType = null;
      this.loading.categories = false;
    },

    async deleteCategory(id) {
      this.loading.categories = true;

      const additionalCategoriesIds = this.additionalCategories.map(
        cat => cat.id
      );
      const foundIndex = additionalCategoriesIds.findIndex(el => el === id);

      if (foundIndex > -1) {
        additionalCategoriesIds.splice(foundIndex, 1);
        const params = {
          additional_categories: additionalCategoriesIds
        };

        gtmEvent__singleLocationEditSummary({
          locationId: this.$route.params.id,
          fields: "additional_categories"
        });

        await this.saveFacilityDetails(params);
      }
      this.loading.categories = false;
    },

    // ---------------------------------------
    // LABELS
    showLabelDeleteConfirmationDialog(index) {
      this.deleteLabelIndex = index;
      this.dialogDeleteLabel = true;
    },

    hideLabelDeleteConfirmationDialog() {
      this.deleteLabelIndex = null;
      this.dialogDeleteLabel = false;
    },

    async saveLabels(labels) {
      this.loading.labels = true;
      this.labels = labels;

      const payload = {
        labels: this.labels
      };

      gtmEvent__singleLocationEditSummary({
        locationId: this.locationId,
        fields: "labels"
      });

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.SINGLE_LOCATION_EDIT_BASIC_DETAILS,
        location_id: this.locationId,
        field: "labels"
      });

      await this.saveFacilityDetails(payload);
      this.loading.labels = false;
    },

    async deleteLabel(index) {
      if (index >= 0) {
        this.labels.splice(index, 1);
      }
      this.hideLabelDeleteConfirmationDialog();

      await this.saveLabels(this.labels);
    },

    // ---------------------------------------
    // OPENING HOURS
    async saveOpeningHours(days) {
      this.loading.openingHours = true;
      const payload = {};

      for (let [k, v] of Object.entries(days)) {
        const upperKey = upperFirst(k);
        if (v.value.length > 0) {
          payload[upperKey] = v.value;
        }
      }

      gtmEvent__singleLocationEditSummary({
        locationId: this.$route.params.id,
        fields: "opening_hours"
      });

      await this.saveFacilityDetails({ opening_hours: payload });
      this.loading.openingHours = false;

      this.activeDialog = "";
    },

    async saveSpecialHours(days) {
      this.loading.specialOpeningHours = true;
      let payload = [];

      for (let day of days) {
        if (day.is_closed) {
          payload.push({
            is_closed: day.is_closed,
            start_date: day.start_date
          });
        } else {
          for (let item of day.value) {
            payload.push({
              is_closed: day.is_closed,
              close_date: item[1] === "24:00" ? day.start_date : null,
              open_time: item[0],
              close_time: item[1],
              start_date: day.start_date
            });
          }
        }
      }

      gtmEvent__singleLocationEditSummary({
        locationId: this.$route.params.id,
        fields: "special_hours"
      });

      await this.saveFacilityDetails({ special_hours: payload });
      this.loading.specialOpeningHours = false;

      this.activeDialog = "";
    },

    formatOpeningHours(item) {
      return item[0] === "00:00" && item[1] === "24:00"
        ? this.$t("message.facilityDetails.allDayOpeningHours")
        : item.join(" - ");
    },

    formatSpecialHours(items) {
      // Create full date string from object with separate keys e.g from year: 2020, month: 9, day: 15 to "2020-09-15"
      const withFullDate = items.map(item => {
        let fullDate = this.formatSpecialDate(item);

        return {
          ...item,
          formatted_date: fullDate
        };
      });

      // Group objects with the same date
      const grouped = groupBy(withFullDate, "formatted_date");

      const formatted = Object.entries(grouped).map(([k, v]) => {
        let value = [["", ""]];

        if (!v[0].is_closed) {
          value = grouped[k].map(item => {
            return [item.open_time, item.close_time];
          });
        }

        return {
          id: cuid(),
          formatted_date: k,
          value,
          is_closed: v[0].is_closed,
          start_date: v[0].start_date,
          _item: grouped[k]
        };
      });

      return formatted.sort(
        (a, b) => new Date(a.formatted_date) - new Date(b.formatted_date)
      );
    },

    formatSpecialDate(item) {
      const month =
        item.start_date.month.length < 2
          ? `0${item.start_date.month}`
          : item.start_date.month;
      const day =
        item.start_date.day.length < 2
          ? `0${item.start_date.day}`
          : item.start_date.day;

      return moment(`${item.start_date.year}-${month}-${day}`).format(
        "YYYY-MM-DD"
      );
    },

    setMoreHours(moreHours) {
      this.moreHours = [...moreHours].map(item => {
        const days = {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        };

        Object.entries(item.values).forEach(([k, v]) => {
          const lowerKey = k.toLowerCase();
          days[lowerKey] = v;
        });

        return {
          name: item.gmb_more_hours.display_name,
          type: item.gmb_more_hours.hours_type_id,
          id: cuid(),
          values: days
        };
      });
    },

    async saveMoreHours(moreHours) {
      this.loading.moreHours = true;
      const payload = [];

      moreHours.forEach(category => {
        const moreHoursObj = {};
        moreHoursObj.gmb_more_hours = { hours_type_id: category.type };
        moreHoursObj.values = {};

        for (let [k, v] of Object.entries(category.values)) {
          const upperKey = upperFirst(k);
          moreHoursObj.values[upperKey] = v;
        }

        payload.push(moreHoursObj);
      });

      gtmEvent__singleLocationEditSummary({
        locationId: this.$route.params.id,
        fields: "more_hours"
      });

      await this.saveFacilityDetails({ more_hours: payload });
      this.loading.moreHours = false;

      this.activeDialog = "";
    },

    // ---------------------------------------
    // OPEN STATUS
    async saveOpenStatus({ openStatus }) {
      if (this.currentOpenStatus === openStatus) return;

      this.loading.openStatus = true;

      const params = { open_status: openStatus };
      await this.saveFacilityDetails(params);

      this.loading.openStatus = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables.scss";

.map-btns-wrapper {
  display: flex;
  gap: $spacing;
  width: 100%;

  @media (max-width: 1635px) {
    flex-wrap: wrap;
  }
}

.facility-details {
  padding: 0 $spacing * 2;

  &__heading {
    margin-left: $spacing * -1.5;
  }

  &__main-data {
    padding-right: $spacing * 2;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      padding-right: $spacing * 4;
      border-right: 1px solid $light-3;
    }
  }

  &__address-wrapper {
    display: flex;
    flex-direction: column;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      flex-direction: row;
    }
  }

  &__map-field--multiline {
    ::v-deep textarea {
      max-height: 160px;
      overflow-y: auto;
    }
    pointer-events: unset;
  }

  &__map-wrapper {
    margin-bottom: $spacing * 4;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: 100%;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      width: 100%;
      min-width: 250px;
      max-width: 50%;
    }
  }

  &__map {
    margin: auto;
    min-height: 402px;
    width: 100%;
    background-color: $light-4;
    margin-bottom: $spacing * 2.5;
    border-radius: 10px;
    overflow: hidden;
    margin-top: 0;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      width: 100%;
      height: auto;
      min-height: 402px;
      margin-left: 0;
      margin-right: 0;
    }
  }

  &__map-details {
    width: 100%;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      padding-right: $spacing * 4;
    }

    @media #{map-get($display-breakpoints, 'xl-only')} {
      padding-right: $spacing * 7.5;
    }
  }

  &__url-attributes,
  &__attributes {
    position: relative;
    padding-top: $spacing * 3.75;
    padding-left: $spacing * 2;
    margin-left: $spacing * -2;
    border-top: 1px solid $light-3;
  }

  &__additional-data {
    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      padding-left: $spacing * 5.5;
    }
  }

  &__labels,
  &__opening-hours,
  &__custom-opening-hours {
    position: relative;
    margin-bottom: $spacing * 5;

    li {
      list-style: none;
    }
  }

  &__open-status {
    position: relative;
  }
}

.section:not(:first-child) {
  margin-top: $spacing * 4;
}

.section-title {
  @include sectionTitle();
}

.add-attributes {
  padding: $spacing * 3.5 $spacing * 4.5;
  background: $light-4;
  border-radius: 20px;

  .description {
    @include font-size(12px);
    color: $grey-darker;
    opacity: 0.7;
    font-weight: 400;

    &__title {
      @include font-size(14px);
      line-height: 17px;
      color: $grey-darker;
      margin-bottom: $spacing;
      font-weight: 500;
    }
  }

  .add-url-attributes-message,
  .add-attributes-message {
    max-width: 435px;
    margin-right: $spacing;
  }

  .v-btn {
    text-transform: none;
  }
}

.v-chip {
  @include vChip();
  white-space: pre-wrap;

  &.status--1 {
    color: $cyan;
  }

  &.status--2 {
    color: $pink;
  }

  &.status--3 {
    color: $lime;
  }

  &.status--general {
    width: 100%;
    background-color: $light-4;
  }
}

.mr--1 {
  margin-right: $spacing * -0.75;
}
</style>
